import {
  Dialog,
  DialogButtonGroup,
  DialogButton,
  DialogHeading,
} from "../Dialog";
import { useTimer } from "use-timer";
import styles from "./SessionTimeoutDialog.module.scss";
import { useLogoutId, useUrls } from "../../app/Providers/AppContext";

type SessionTimeoutWarningProps = {
  /** Initial time to display in the countdown timer */
  initialTime: number;

  /** Function to call when the user clicks the Extend session button */
  onExtendSession: () => void;
};

export function SessionTimeoutDialog({
  initialTime,
  onExtendSession,
}: SessionTimeoutWarningProps) {
  const urls = useUrls();
  const logoutId = useLogoutId();

  function logout() {
    const logoutUrl = urls.account.logout(window.location.origin, logoutId);
    window.location.assign(logoutUrl);
  }

  // This countdown timer is displayed on the dialog so the user knows how much time they have left in their session.
  const { time } = useTimer({
    // Divide by 1000 since initialTime expects seconds.
    // Since time has an interval of 1 second, set initialTime to 1 second if passed a value lower than that.
    initialTime: initialTime < 1000 ? 1 : initialTime / 1000,
    timerType: "DECREMENTAL",
    autostart: true,
    endTime: 0,
    onTimeOver: logout,
  });

  const timeoutRoundedToNearestTen = Math.ceil(time / 10) * 10;

  return (
    <Dialog
      label="Your session is about to expire."
      className={styles.sessionTimeoutDialog}
      showCloseButton={"no"}
      onDismiss={onExtendSession}
      footer={
        <DialogButtonGroup>
          <DialogButton onClick={onExtendSession}>Extend session</DialogButton>
        </DialogButtonGroup>
      }
    >
      <DialogHeading>Still there?</DialogHeading>

      <p>
        Your session will expire in{" "}
        {/* aria-hidden because the separate timer below that only changes every 10 seconds is soley for screenreaders. */}
        <time role="timer" aria-hidden="true" dateTime={`P${time}S`}>
          {time}
        </time>
        {/* For screenreaders, round countdown to nearest 10 seconds, so it's not obnoxiously telling them every second. */}
        <time
          className="sr-only"
          role="timer"
          aria-live="assertive"
          aria-label={`Your session will timeout in ${timeoutRoundedToNearestTen} seconds`}
          dateTime={`P${timeoutRoundedToNearestTen}S`}
        />{" "}
        seconds.
        <br />
        Click "Extend session" to stay signed in.
      </p>
    </Dialog>
  );
}
